import { v4 as uuidv4 } from 'uuid';

import { type DropdownListItem } from '@verantos-dev/shared-components';
import {
  DerivedEventInputFieldTypeLegacy,
  DerivedEventTypeLegacy,
  type InputFieldLegacy,
  InputFieldTypeLegacy,
  type InputOptionContextDataLegacy,
  type DerivedEventFilterLegacy,
  type InputFieldConfig,
} from '@verantos-dev/types-cohorts';
import {
  Sequence_event_criterionContext,
  Merged_event_criterionContext,
  Instance_event_criterionContext,
} from '@verantos-dev/util-cohort-builder-typescript-runtime';

import {
  TOKEN_OFFSET,
  COMPONENT_COMBOBOX_MEDIUM,
  COMPONENT_DROPDOWN_WIDE,
  COMPONENT_RADIO,
  COMPONENT_DEFAULT,
  COMPONENT_NUMERIC_INTEGER,
  COMPONENT_DROPDOWN_NARROW,
} from '../../constants/constants';
import { tokenOptionsForTokenMap } from '../../constants/runtimeMaps';
import { getCurrentCohortEventNames } from '../../utils/getCurrentCohortEventNames';

type NextEventFilters = {
  nextDerivedEventFilters: DerivedEventFilterLegacy[];
  nextDerivedEventFilterConfigs: Record<string, InputFieldConfig>;
};

export const getNextEventFilters = (
  eventFilters: DerivedEventFilterLegacy[],
  eventFilterContexts:
    | Sequence_event_criterionContext[]
    | Merged_event_criterionContext[]
    | Instance_event_criterionContext[],
  codeCompletionOptions: string[][],
  inputOptionContextData: InputOptionContextDataLegacy
): NextEventFilters => {
  const nextDerivedEventFilters: DerivedEventFilterLegacy[] = [];
  const nextDerivedEventFilterConfigs: Record<string, InputFieldConfig> = {};

  const { activeCohortBuilderItem, currentCohortDefinition } = inputOptionContextData;
  const eventNames = getCurrentCohortEventNames(activeCohortBuilderItem, currentCohortDefinition);
  // Obtain the currently referenced event names for the current derived event filters
  const eventFilterNames = eventFilters.map((eventFilter) => eventFilter.eventName.value || '');

  // Loop over each event filter and update the input field & obtain the input field config
  eventFilters.forEach((eventFilter, i) => {
    const filterIndex = (eventFilterContexts[i]?.start?.tokenIndex || 0) + TOKEN_OFFSET;

    // Get the next event name input field & config
    const eventNameCodeCompletionOptions = codeCompletionOptions[filterIndex];
    const {
      nextDerivedEventInputField: nextEventName,
      nextDerivedEventInputFieldConfig: nextEventNameInputFieldConfig,
    } = getNextEventFilter(
      i,
      DerivedEventInputFieldTypeLegacy.EVENT_NAME,
      eventFilter,
      eventNameCodeCompletionOptions,
      {
        ...inputOptionContextData,
        eventNames, // pass the possible names for selection
        eventFilterNames, // pass the currently referenced event names to prevent duplicate event names auto populating
        nextDerivedEventFilters, // pass the next derived event filters to prevent duplicate event names auto populating
      }
    );
    nextDerivedEventFilterConfigs[nextEventName.id] = nextEventNameInputFieldConfig;

    // Get the next date attribute input field & config
    const dateAttributeCodeCompletionOptions = codeCompletionOptions[filterIndex + 2];
    const {
      nextDerivedEventInputField: nextDateAttribute,
      nextDerivedEventInputFieldConfig: nextDateAttributeInputFieldConfig,
    } = getNextEventFilter(
      i,
      DerivedEventInputFieldTypeLegacy.EVENT_DATE_ATTRIBUTE,
      eventFilter,
      dateAttributeCodeCompletionOptions,
      inputOptionContextData
    );
    nextDerivedEventFilterConfigs[nextDateAttribute.id] = nextDateAttributeInputFieldConfig;

    // Get the next event selection input field & config, if sequence event type
    const nextEventSelectionFieldAndConfig =
      inputOptionContextData.derivedEventType === DerivedEventTypeLegacy.SEQUENCE
        ? getNextEventFilter(
            i,
            DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED,
            eventFilter,
            [],
            inputOptionContextData
          )
        : undefined;
    nextEventSelectionFieldAndConfig &&
      (nextDerivedEventFilterConfigs[
        nextEventSelectionFieldAndConfig.nextDerivedEventInputField.id
      ] = nextEventSelectionFieldAndConfig.nextDerivedEventInputFieldConfig);

    // Get the next position input field & config, if instance event type
    const positionCodeCompletionOptions = codeCompletionOptions[filterIndex + 7];
    const nextPositionFieldAndConfig =
      inputOptionContextData.derivedEventType === DerivedEventTypeLegacy.INSTANCE
        ? getNextEventFilter(
            i,
            DerivedEventInputFieldTypeLegacy.EVENT_POSITION,
            eventFilter,
            positionCodeCompletionOptions,
            inputOptionContextData
          )
        : undefined;
    nextPositionFieldAndConfig &&
      (nextDerivedEventFilterConfigs[nextPositionFieldAndConfig.nextDerivedEventInputField.id] =
        nextPositionFieldAndConfig.nextDerivedEventInputFieldConfig);

    // Get the next index input field & config, if sequence event type
    const indexCodeCompletionOptions = codeCompletionOptions[filterIndex + 9];
    const nextIndexFieldAndConfig =
      inputOptionContextData.derivedEventType === DerivedEventTypeLegacy.INSTANCE &&
      eventFilter.position?.value === 'INDEX'
        ? getNextEventFilter(
            i,
            DerivedEventInputFieldTypeLegacy.EVENT_INDEX,
            eventFilter,
            indexCodeCompletionOptions,
            inputOptionContextData
          )
        : undefined;
    nextIndexFieldAndConfig &&
      (nextDerivedEventFilterConfigs[nextIndexFieldAndConfig.nextDerivedEventInputField.id] =
        nextIndexFieldAndConfig.nextDerivedEventInputFieldConfig);

    // Add the next event filter to the list of next event filters
    nextDerivedEventFilters.push({
      ...eventFilter,
      eventName: nextEventName,
      eventDateAttribute: nextDateAttribute,
      isSelected:
        nextEventSelectionFieldAndConfig &&
        nextEventSelectionFieldAndConfig.nextDerivedEventInputField,
      position: nextPositionFieldAndConfig && nextPositionFieldAndConfig.nextDerivedEventInputField,
      index: nextIndexFieldAndConfig && nextIndexFieldAndConfig.nextDerivedEventInputField,
    });
  });

  return {
    nextDerivedEventFilters,
    nextDerivedEventFilterConfigs,
  };
};

type NewEventFilter = {
  nextDerivedEventInputField: InputFieldLegacy;
  nextDerivedEventInputFieldConfig: InputFieldConfig;
};

const getNextEventFilter = (
  filterIndex: number,
  derivedInputFieldType: DerivedEventInputFieldTypeLegacy,
  eventFilter: DerivedEventFilterLegacy,
  options: string[],
  inputOptionContextData: InputOptionContextDataLegacy
): NewEventFilter => {
  const nextOptions = getInputOptionsForDerivedEventFilter(
    filterIndex,
    options,
    inputOptionContextData
  );
  const nextInputType = getInputTypeForDerivedEventFilter(derivedInputFieldType);

  return {
    nextDerivedEventInputField:
      getNextEventFilterInputField(
        derivedInputFieldType,
        eventFilter,
        nextInputType,
        nextOptions
      ) ?? '',
    nextDerivedEventInputFieldConfig: {
      inputType: nextInputType,
      options: nextOptions,
      label: getLabelForDerivedEventFilter(derivedInputFieldType),
      placeholder: getPlaceholderForDerivedEventFilter(derivedInputFieldType),
      width: getWidthForDerivedEventFilter(derivedInputFieldType),
      disabled: getDisabledForDerivedEventFilter(derivedInputFieldType, eventFilter),
      minValue: getMinValueForDerivedEventFilter(derivedInputFieldType),
      minValueMsg: getMinValueMsgForDerivedEventFilter(derivedInputFieldType),
    },
  };
};

const getValueForDerivedEventInputFieldType = (
  derivedInputFieldType: DerivedEventInputFieldTypeLegacy,
  eventFilter: DerivedEventFilterLegacy
): InputFieldLegacy => {
  switch (derivedInputFieldType) {
    case DerivedEventInputFieldTypeLegacy.EVENT_NAME:
      return { id: eventFilter.eventName.id, value: eventFilter.eventName.value };
    case DerivedEventInputFieldTypeLegacy.EVENT_DATE_ATTRIBUTE:
      return { id: eventFilter.eventDateAttribute.id, value: eventFilter.eventDateAttribute.value };
    case DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED:
      return {
        id: eventFilter.isSelected?.id ?? uuidv4(),
        value: eventFilter.isSelected?.value ?? '',
      };
    case DerivedEventInputFieldTypeLegacy.EVENT_POSITION:
      return { id: eventFilter.position?.id ?? uuidv4(), value: eventFilter.position?.value ?? '' };
    case DerivedEventInputFieldTypeLegacy.EVENT_INDEX:
      return {
        id: !eventFilter.index || eventFilter.index?.id === '' ? uuidv4() : eventFilter.index?.id,
        value: eventFilter.index?.value ?? '',
      };
  }
};

const getNextEventFilterInputField = (
  derivedInputFieldType: DerivedEventInputFieldTypeLegacy,
  eventFilter: DerivedEventFilterLegacy,
  nextInputType: InputFieldTypeLegacy,
  nextOptions: DropdownListItem[] | DropdownListItem[]
): InputFieldLegacy => {
  const nextValue = getValueForDerivedEventInputFieldType(derivedInputFieldType, eventFilter);
  // If the input field is a combo box or dropdown
  if ([InputFieldTypeLegacy.COMBO_BOX, InputFieldTypeLegacy.DROPDOWN].includes(nextInputType)) {
    // has only one option, set the value to the option value
    if (nextOptions?.length === 1 && nextOptions[0].value) {
      return { ...nextValue, value: nextOptions[0].value, dirty: true };
    }

    // clear the input value if no option matches current value selection
    if (
      !nextOptions?.some((option) => {
        return option.value === nextValue.value;
      })
    ) {
      return { ...nextValue, value: '', dirty: true };
    }
  }

  return { ...nextValue, dirty: true };
};

const getInputTypeForDerivedEventFilter = (
  derivedInputFieldType: DerivedEventInputFieldTypeLegacy
): InputFieldTypeLegacy => {
  // Event name input field
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_NAME) {
    return InputFieldTypeLegacy.COMBO_BOX;
  }

  // Date attribute input field
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_DATE_ATTRIBUTE) {
    return InputFieldTypeLegacy.DROPDOWN;
  }

  // Event selection input field
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED) {
    return InputFieldTypeLegacy.CHECKBOX;
  }

  // Position input field
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_POSITION) {
    return InputFieldTypeLegacy.DROPDOWN;
  }

  // Index input field
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_INDEX) {
    return InputFieldTypeLegacy.INT_NUMERIC_FIELD;
  }

  return InputFieldTypeLegacy.DROPDOWN;
};

const getInputOptionsForDerivedEventFilter = (
  filterIndex: number,
  options: string[],
  { eventNames, eventFilterNames, derivedEventType }: InputOptionContextDataLegacy
): DropdownListItem[] | DropdownListItem[] => {
  const nextOptions = options.reduce((accu: DropdownListItem[], optionToken) => {
    const optionsForToken: DropdownListItem[] = tokenOptionsForTokenMap[optionToken]?.map(
      ({ displayValue, grammarValue }) => ({
        value: grammarValue,
        displayValue,
      })
    );
    return [...accu, ...(optionsForToken ?? [])];
  }, []);

  // Obtain the list of valid drop down options for the event name input field
  // inject event names for each simple and derived events
  if (
    nextOptions.some((option) =>
      [
        'QUALIFIED_CONDITION_OCCURRENCE_ALIAS',
        'QUALIFIED_DRUG_EXPOSURE_ALIAS',
        'QUALIFIED_MEASUREMENT_ALIAS',
        'QUALIFIED_OBSERVATION_ALIAS',
        'QUALIFIED_PROCEDURE_OCCURRENCE_ALIAS',
        'QUALIFIED_VISIT_OCCURRENCE_ALIAS',
        'QUALIFIED_DERIVED_EVENT_NAME',
      ].includes(option.value ?? '')
    )
  ) {
    // If this is a merged event we need to filter out any event names that have already been used in another event filter
    // Otherwise, we can include all eventNames
    let validEventNames: DropdownListItem[] = eventNames || [];
    if (derivedEventType === DerivedEventTypeLegacy.MERGED) {
      // Find the event names that are used in filters besides this one
      const eventNamesUsedInOtherFilters = eventFilterNames?.filter((_, i) => i !== filterIndex);
      if (eventNamesUsedInOtherFilters) {
        validEventNames = validEventNames.filter(
          (eventName) => !eventNamesUsedInOtherFilters.includes(eventName.value || '')
        );
      }
    }

    // For instance events, we should exclude other instance events
    if (derivedEventType === DerivedEventTypeLegacy.INSTANCE) {
      const nonInstanceEventNames = validEventNames.filter(
        (eventName) => !eventName.value?.includes('INSTANCE_EVENT::')
      );
      validEventNames = nonInstanceEventNames;
    }

    return validEventNames;
  }

  return nextOptions;
};

const getLabelForDerivedEventFilter = (
  derivedInputFieldType: DerivedEventInputFieldTypeLegacy
): string => {
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_NAME) {
    return 'Event';
  }
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_DATE_ATTRIBUTE) {
    return 'Date attribute';
  }
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED) {
    return 'Use as event date';
  }
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_POSITION) {
    return 'Position';
  }
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_INDEX) {
    return 'Index';
  }

  return 'Attribute';
};

const getPlaceholderForDerivedEventFilter = (
  derivedInputFieldType: DerivedEventInputFieldTypeLegacy
): string => {
  switch (derivedInputFieldType) {
    case DerivedEventInputFieldTypeLegacy.EVENT_NAME: {
      return 'Select event';
    }
    case DerivedEventInputFieldTypeLegacy.EVENT_DATE_ATTRIBUTE: {
      return 'Select date attribute';
    }
    case DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED: {
      return '';
    }
    case DerivedEventInputFieldTypeLegacy.EVENT_POSITION: {
      return '';
    }
    case DerivedEventInputFieldTypeLegacy.EVENT_INDEX: {
      return '1';
    }
  }
};

const getWidthForDerivedEventFilter = (
  derivedInputFieldType: DerivedEventInputFieldTypeLegacy
): number => {
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_NAME) {
    return COMPONENT_COMBOBOX_MEDIUM;
  }
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_DATE_ATTRIBUTE) {
    return COMPONENT_DROPDOWN_WIDE;
  }

  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED) {
    return COMPONENT_RADIO;
  }

  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_POSITION) {
    return COMPONENT_DROPDOWN_NARROW;
  }

  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_INDEX) {
    return COMPONENT_NUMERIC_INTEGER;
  }

  return COMPONENT_DEFAULT;
};

const getDisabledForDerivedEventFilter = (
  derivedInputFieldType: DerivedEventInputFieldTypeLegacy,
  eventFilter: DerivedEventFilterLegacy
): boolean => {
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_DATE_ATTRIBUTE) {
    return eventFilter.eventName.value === '';
  }

  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_POSITION) {
    return eventFilter.eventName.value === '' || eventFilter.eventDateAttribute.value === '';
  }

  return false;
};

const getMinValueForDerivedEventFilter = (
  derivedInputFieldType: DerivedEventInputFieldTypeLegacy
): number | undefined => {
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_INDEX) {
    return 1;
  }

  return undefined;
};

const getMinValueMsgForDerivedEventFilter = (
  derivedInputFieldType: DerivedEventInputFieldTypeLegacy
): string | undefined => {
  if (derivedInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_INDEX) {
    return 'Min value is 1';
  }

  return undefined;
};
